import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-play-circle-fill text-background-50 text-xl"></i>
            </div>
            <span className={`font-heading text-lg font-bold ${isScrolled || !isHome ? "text-foreground-900" : "text-white"}`}>
              {t("site_name")}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname === "/"
                  ? isScrolled || !isHome ? "text-primary-500" : "text-white"
                  : isScrolled || !isHome ? "text-foreground-700 hover:text-primary-500" : "text-white/80 hover:text-white"
              }`}
            >
              {t("nav_home")}
            </Link>

            <div className="relative group">
              <Link
                to="/category/hot"
                className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1 ${
                  location.pathname.startsWith("/category")
                    ? isScrolled || !isHome ? "text-primary-500" : "text-white"
                    : isScrolled || !isHome ? "text-foreground-700 hover:text-primary-500" : "text-white/80 hover:text-white"
                }`}
              >
                {t("nav_category")}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </Link>
              <div className="absolute top-full left-0 mt-1 py-2 bg-background-50 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[140px]">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="block px-4 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname === "/about"
                  ? isScrolled || !isHome ? "text-primary-500" : "text-white"
                  : isScrolled || !isHome ? "text-foreground-700 hover:text-primary-500" : "text-white/80 hover:text-white"
              }`}
            >
              {t("nav_about")}
            </Link>
          </div>

          <div className="hidden md:block">
            <a
              href="#download"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer"
            >
              <i className="ri-download-line"></i>
              {t("nav_download")}
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${isScrolled || !isHome ? "text-foreground-900" : "text-white"} ${isMobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 py-2 cursor-pointer">
              {t("nav_home")}
            </Link>
            <div className="text-sm font-medium text-foreground-500 py-2">{t("nav_category")}</div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="text-sm text-foreground-600 hover:text-primary-500 pl-4 py-1 cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="text-sm font-medium text-foreground-700 hover:text-primary-500 py-2 cursor-pointer">
              {t("nav_about")}
            </Link>
            <a
              href="#download"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer"
            >
              <i className="ri-download-line"></i>
              {t("nav_download")}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}